import { AlertService } from '@anecdotes/design-system/alert';
import { ModalWindowService } from '@anecdotes/design-system/modals';
import { ChangeDetectionStrategy, Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subject } from 'rxjs';
import { TrustCenterFacadeService, RequirementsCategoryResponse, RequirementResponse, RequirementUpdateRequest } from '@anecdotes/shared-trust-center';
import { RequirementEditComponent } from '../requirement-edit/requirement-edit.component';
import { RadioButtonModel } from '@anecdotes/design-system/form-controls';
import { FormControl } from '@angular/forms';
import { sortAlphabetically } from '@anecdotes/utils';

@Component({
  selector: 'app-control-edit-side-panel',
  templateUrl: './control-edit-side-panel.component.html',
  styleUrls: ['./control-edit-side-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ControlEditSidePanelComponent implements OnInit {
  @ViewChildren('requirementEditor')
  private requirementEditors: QueryList<RequirementEditComponent>;

  readonly linkedFrameworksRadioButtons: RadioButtonModel[] = [
    {
      id: 'yes',
      icon: 'linked',
      value: true,
      label: this.buildTranslationKey('linkedFrameworksRadioGroup.yes'),
    },
    {
      id: 'no',
      value: false,
      label: this.buildTranslationKey('linkedFrameworksRadioGroup.no'),
    },
  ];

  readonly monitoredIndicationRadioButtons: RadioButtonModel[] = [
    {
      id: 'yes',
      icon: 'monitored',
      value: true,
      label: this.buildTranslationKey('monitoredIndicationRadioGroup.yes'),
    },
    {
      id: 'no',
      value: false,
      label: this.buildTranslationKey('monitoredIndicationRadioGroup.no'),
    },
  ];

  readonly linkedFrameworksDisplayingControl = new FormControl<boolean>(undefined);

  readonly monitoredIndicationDisplayingControl = new FormControl<boolean>(undefined);

  readonly sendingRequestBtnLoader$ = new Subject<boolean>();

  @Input()
  control: RequirementsCategoryResponse;

  requirements: RequirementResponse[];

  get canSave(): boolean {
    if (!this.requirementEditors) {
      return false;
    }

    const anyFormChanged = this.requirementEditors.filter((editor) => editor.wasFormValueChanged).length !== 0;
    const allFormsValid = this.requirementEditors.filter((editor) => editor.form.invalid).length === 0;
    const displayingLinkedFrameworksChanged =
      this.linkedFrameworksDisplayingControl.value !== this.control.show_linked_controls;
    const displayingMonitoredIndicationChanged =
      this.monitoredIndicationDisplayingControl.value !== this.control.show_monitoring;

    return (
      (anyFormChanged || displayingLinkedFrameworksChanged || displayingMonitoredIndicationChanged) && allFormsValid
    );
  }

  constructor(
    private trustCenterFacadeService: TrustCenterFacadeService,
    private modalWindowService: ModalWindowService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.linkedFrameworksDisplayingControl.setValue(this.control.show_linked_controls);
    this.monitoredIndicationDisplayingControl.setValue(this.control.show_monitoring);

    this.requirements = this.control.requirements.sort((requirement1, requirement2) => {
      if (requirement1.is_visible === requirement2.is_visible) {
        return sortAlphabetically(requirement1.name, requirement2.name);
      }

      return (+requirement2.is_visible) - (+requirement1.is_visible);
    });
  }

  buildTranslationKey(relativeKey: string): string {
    return `trustCenter.${relativeKey}`;
  }

  async saveBtnClick(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    this.sendingRequestBtnLoader$.next(true);

    const updateData: RequirementUpdateRequest[] = this.requirementEditors.map((editor) => editor.formValue);

    try {
      await this.trustCenterFacadeService.updateCategory(this.control.name, {
        requirements: updateData,
        show_linked_controls: this.linkedFrameworksDisplayingControl.value,
        show_monitoring: this.monitoredIndicationDisplayingControl.value,
      });
      this.modalWindowService.close();
    } catch {
      this.alertService.openActionErrorAlert({
        customBaseTranslationKey: this.buildTranslationKey('tryAgainError'),
      });
    } finally {
      this.sendingRequestBtnLoader$.next(false);
    }
  }

  cancel(): void {
    this.modalWindowService.close();
  }
}
